import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/shadcn/alert-dialog';
import { Button } from '@/components/shadcn/button';
import { cn } from '@/utils/cn';

type PhotoGalleryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photos: string[];
  initialIndex?: number;
  title?: string;
};

const MIN_SWIPE_DISTANCE_PX = 10;

export default function PhotoGalleryModal({
  open,
  onOpenChange,
  photos,
  initialIndex = 0,
  title = 'Galeri Foto',
}: PhotoGalleryModalProps) {
  const safeInitialIndex = useMemo(() => {
    if (!photos.length) return 0;
    return Math.min(Math.max(initialIndex, 0), photos.length - 1);
  }, [initialIndex, photos.length]);

  const [activeIndex, setActiveIndex] = useState<number>(safeInitialIndex);
  const [mainLoaded, setMainLoaded] = useState<Record<number, boolean>>({});
  const [thumbLoaded, setThumbLoaded] = useState<Record<number, boolean>>({});
  const thumbsRef = useRef<HTMLDivElement>(null);

  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < photos.length - 1;

  const goPrev = () => setActiveIndex((i) => Math.max(0, i - 1));
  const goNext = () => setActiveIndex((i) => Math.min(photos.length - 1, i + 1));

  useEffect(() => {
    if (!open) return;
    setActiveIndex(safeInitialIndex);
  }, [open, safeInitialIndex]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && canPrev) goPrev();
      if (e.key === 'ArrowRight' && canNext) goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, canPrev, canNext]);

  useEffect(() => {
    if (!open) return;
    const container = thumbsRef.current;
    if (!container) return;
    const activeEl = container.children[activeIndex] as HTMLElement | undefined;
    if (!activeEl) return;

    const containerWidth = container.clientWidth;
    const elWidth = activeEl.offsetWidth;
    const elLeft = activeEl.offsetLeft;
    const scrollLeft = elLeft - containerWidth / 2 + elWidth / 2;

    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }, [open, activeIndex]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (touchStartX == null || touchEndX == null) return;
    const distance = touchStartX - touchEndX;
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE_PX;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE_PX;

    if (isLeftSwipe && canNext) goNext();
    if (isRightSwipe && canPrev) goPrev();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className={cn(
          'p-0 border-0 bg-black text-white overflow-hidden',
          // mobile-first: near-fullscreen
          'w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] h-[85vh] rounded-2xl',
          // desktop: larger canvas
          'sm:w-[calc(100vw-4rem)] sm:max-w-5xl sm:h-[80vh] sm:rounded-3xl'
        )}
      >
        <AlertDialogTitle className="sr-only">{title}</AlertDialogTitle>
        <AlertDialogDescription className="sr-only">
          Popup galeri foto dengan navigasi.
        </AlertDialogDescription>

        <div className="flex h-full flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between gap-3 px-3 py-3 sm:px-4">
            <div className="text-xs font-medium text-white/80">
              {photos.length ? `${activeIndex + 1} / ${photos.length}` : '0 / 0'}
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-9 w-9 rounded-full bg-white/10 px-0 hover:bg-white/20"
              aria-label="Tutup galeri foto"
            >
              <Icon icon="mdi:close" className="h-5 w-5" />
            </Button>
          </div>

          {/* Main image */}
          <div className="relative flex-1 px-3 pb-3 sm:px-4 sm:pb-4">
            <div
              className="relative h-full w-full overflow-hidden rounded-xl bg-white/5 sm:rounded-2xl touch-pan-y select-none"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {!photos.length ? (
                <div className="flex h-full w-full items-center justify-center text-sm text-white/60">
                  Belum ada foto.
                </div>
              ) : (
                <>
                  {!mainLoaded[activeIndex] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/5">
                      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
                    </div>
                  )}

                  <img
                    src={photos[activeIndex]}
                    alt={`Foto galeri ${activeIndex + 1}`}
                    className={cn(
                      'absolute inset-0 h-full w-full object-contain transition-opacity duration-300',
                      mainLoaded[activeIndex] ? 'opacity-100' : 'opacity-0'
                    )}
                    onLoad={() => setMainLoaded((p) => ({ ...p, [activeIndex]: true }))}
                    draggable={false}
                  />

                  {/* Mobile nav buttons (inside image) */}
                  {canPrev && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={goPrev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 px-0 hover:bg-black/70 sm:hidden"
                      aria-label="Foto sebelumnya"
                    >
                      <Icon icon="mdi:chevron-left" className="h-6 w-6" />
                    </Button>
                  )}
                  {canNext && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={goNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 px-0 hover:bg-black/70 sm:hidden"
                      aria-label="Foto selanjutnya"
                    >
                      <Icon icon="mdi:chevron-right" className="h-6 w-6" />
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Desktop nav buttons (outside image) */}
            {photos.length > 0 && (
              <>
                {canPrev && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={goPrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 hidden h-11 w-11 rounded-full bg-black/50 px-0 hover:bg-black/70 sm:flex"
                    aria-label="Foto sebelumnya"
                  >
                    <Icon icon="mdi:chevron-left" className="h-7 w-7" />
                  </Button>
                )}
                {canNext && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={goNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 hidden h-10 w-10 rounded-full bg-black/50 px-0 hover:bg-black/70 sm:flex"
                    aria-label="Foto selanjutnya"
                  >
                    <Icon icon="mdi:chevron-right" className="h-7 w-7" />
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Thumbnails */}
          {photos.length > 0 && (
            <div className="px-3 pb-3 sm:px-4 sm:pb-4">
              <div
                ref={thumbsRef}
                className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                {photos.map((photo, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={`${photo}-${idx}`}
                      type="button"
                      onClick={() => setActiveIndex(idx)}
                      className={cn(
                        'relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all sm:h-20 sm:w-20',
                        isActive
                          ? 'border-white'
                          : 'border-transparent opacity-70 hover:opacity-100 hover:border-white/30'
                      )}
                      aria-label={`Lihat foto ${idx + 1}`}
                    >
                      {!thumbLoaded[idx] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/10">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white/70" />
                        </div>
                      )}
                      <img
                        src={photo}
                        alt={`Thumbnail ${idx + 1}`}
                        className={cn(
                          'h-full w-full object-cover transition-opacity duration-300',
                          thumbLoaded[idx] ? 'opacity-100' : 'opacity-0'
                        )}
                        onLoad={() => setThumbLoaded((p) => ({ ...p, [idx]: true }))}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
